import { h, render } from 'preact';
import stylesUrl from './styles.css?inline';
import { makeT } from './i18n.js';
import { ChatBody } from './components/ChatBody.js';
import { resolveLanguage } from './utils/lang.js';
import { Tracking } from './utils/tracking.js';
import { getSharedTokenFromHash } from './utils/urlHash.js';
import { injectTurnstileScript } from './utils/turnstile.js';

let MTA_STYLES = stylesUrl || '';

export class MTAElement extends HTMLElement {
  static activeDrawers = new Set();

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._open = false;
    this._hasSavedState = false;
    try {
      const saved = sessionStorage.getItem('mta_chat_open');
      if (saved !== null) {
        this._open = saved === 'true';
        this._hasSavedState = true;
      }
    } catch (e) { }
    this._initialPrompt = null;
    this._api = null;
    this._config = {};
    this._sharedToken = null;
  }

  static get observedAttributes() {
    return ['language'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'language' && oldValue !== newValue) {
      const resolved = resolveLanguage(newValue || this._config.language || 'auto');
      this.setAttribute('lang', resolved);
      this._render();
    }
  }

  connectedCallback() {
    // Clear any accidental Light DOM text/content (like FOUC text) inserted by CMS or user.
    this.innerHTML = '';

    Tracking.init();
    this._handleHashChange = this._handleHashChange.bind(this);
    window.addEventListener('hashchange', this._handleHashChange);
    
    // Apply configuration immediately on mount if present to avoid transition flashes.
    if (Object.keys(this._config).length === 0) {
      const activeConfig = (window.MTA && window.MTA._config) || window.MTAConfig;
      if (activeConfig) {
        this._config = { debug: false, ...activeConfig };
        this._syncConfigToAttributes();
        
        if (!this._hasSavedState && this._config.startOpen === true) {
          this._open = true;
          Tracking.trackOpen(this._config.client);
        }
        this._applyTheme();

        injectTurnstileScript(this._config.turnstileSiteKey);

        const token = getSharedTokenFromHash();
        if (token) {
          this._config.startOpen = true;
          this._open = true;
          this._sharedToken = token;
        }
      }
    }

    const orientation = this._config.orientation || 'inline';
    if (orientation === 'drawer') {
      const activeDrawers = MTAElement.activeDrawers;
      if (activeDrawers.size > 0 && !activeDrawers.has(this)) {
        this.style.display = 'none';
        return;
      }
      activeDrawers.add(this);
    }

    this._render();
  }

  disconnectedCallback() {
    if (this._handleHashChange) {
      window.removeEventListener('hashchange', this._handleHashChange);
    }
    MTAElement.activeDrawers.delete(this);
    this.close();
  }

  _handleHashChange() {
    const token = getSharedTokenFromHash();
    if (token) {
      this._config.startOpen = true;
      this._open = true;
      this._sharedToken = token;
      this._mountKey = (this._mountKey || 0) + 1; // Force fresh remount
      this._render();
    }
  }

  _syncConfigToAttributes() {
    const attrLang = this.getAttribute('language');
    const baseLanguage = attrLang || this._config.language || 'auto';
    const resolvedLanguage = resolveLanguage(baseLanguage);
    this.setAttribute('lang', resolvedLanguage);
    const orientation = this._config.orientation === 'drawer' ? 'drawer' : 'inline';
    this.setAttribute('data-orientation', orientation);
    if (this._config.colorScheme) {
      this.setAttribute('color-scheme', this._config.colorScheme);
    } else {
      this.removeAttribute('color-scheme');
    }
  }

  setConfig(config) {
    this._config = { debug: false, ...(config || {}) };
    this._syncConfigToAttributes();
    
    if (!this._hasSavedState && this._config.startOpen === true) {
      this._open = true;
      Tracking.trackOpen(this._config.client);
    }
    this._applyTheme();

    const orientation = this._config.orientation === 'drawer' ? 'drawer' : 'inline';
    if (orientation === 'drawer') {
      const activeDrawers = MTAElement.activeDrawers;
      if (activeDrawers.size > 0 && !activeDrawers.has(this)) {
        this.style.display = 'none';
        return;
      }
      activeDrawers.add(this);
    } else {
      MTAElement.activeDrawers.delete(this);
      if (this.style.display === 'none') {
        this.style.display = '';
      }
    }

    const token = getSharedTokenFromHash();
    if (token) {
      this._config.startOpen = true;
      this._open = true;
      this._sharedToken = token;
    }

    this._render();
  }

  _applyTheme() {
    const theme = this._config.theme || {};
    for (const [name, value] of Object.entries(theme)) {
      const cssVar = name.startsWith('--') ? name : (name.startsWith('mta-') ? `--${name}` : `--mta-${name}`);
      this.style.setProperty(cssVar, value);
    }
  }

  _render() {
    const isDrawer = (this._config.orientation || 'inline') === 'drawer';
    const showFab = isDrawer && this._config.showLauncherButton !== false && !this._open;

    if (!this._mounted) {
      this.shadowRoot.innerHTML = `
        <style>${MTA_STYLES}</style>
        <div class="mta-shell ${this._open ? 'is-open' : ''}">
          <div class="mta-shell-inner">
            <div class="mta-shell-content"></div>
          </div>
        </div>
        <button class="mta-fab ${showFab ? 'is-visible' : ''}" type="button" aria-label="Ask assistant">
          <div class="mta-fab-icon-container">
            <svg class="mta-fab-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
              <path d="M12 7l1 3 3 1-3 1-1 3-1-3-3-1 3-1 1-3z"/>
              <path d="M19 4l.5 1.5 1.5.5-1.5.5-.5 1.5-.5-1.5-1.5-.5 1.5-.5.5-1.5z"/>
            </svg>
            <span class="mta-fab-online-dot"></span>
          </div>
          <span class="mta-fab-text">Ask Assistant</span>
          <div class="mta-fab-send-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </div>
        </button>
      `;
      this._mountTarget = this.shadowRoot.querySelector('.mta-shell-content');
      this._shell = this.shadowRoot.querySelector('.mta-shell');
      this._fab = this.shadowRoot.querySelector('.mta-fab');
      this._fab.addEventListener('click', () => this.toggle());
      this._mounted = true;
    } else {
      this._shell.classList.toggle('is-open', this._open);
      this._fab.classList.toggle('is-visible', showFab);
    }

    // Dynamically update FAB text based on config client name
    if (this._fab) {
      const clientName = this._config.client || '';
      const fabTextEl = this._fab.querySelector('.mta-fab-text');
      if (fabTextEl) {
        fabTextEl.textContent = clientName ? `Ask ${clientName}` : 'Ask Assistant';
      }
    }

    if (this._open) {
      const resolvedLanguage = this.getAttribute('lang') || 'en';
      const t = makeT(resolvedLanguage, this._config.labels);
      render(
        h(ChatBody, {
          key: this._mountKey,
          config: this._config,
          t,
          initialPrompt: this._initialPrompt,
          shadowRoot: this.shadowRoot,
          sharedToken: this._sharedToken,
          onClose: () => {
            this.close();
          },
          onReady: (api) => {
            this._api = api;
          },
        }),
        this._mountTarget
      );
    } else {
      render(null, this._mountTarget);
    }
  }

  _scrollIntoViewIfNeeded() {
    if ((this._config?.orientation || 'inline') === 'drawer') return;
    const rect = this.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const isOffScreen = rect.top > vh - 120 || rect.bottom < 100;
    if (!isOffScreen) return;
    const targetY = Math.max(0, window.scrollY + rect.top - 80);
    window.scrollTo({ top: targetY, behavior: 'smooth' });
  }

  open(initialPrompt) {
    this._initialPrompt = initialPrompt || null;
    this._mountKey = (this._mountKey || 0) + 1;
    this._open = true;
    try {
      sessionStorage.setItem('mta_chat_open', 'true');
    } catch (e) { }
    this._render();
    this._scrollIntoViewIfNeeded();
    this.dispatchEvent(new CustomEvent('mta:open'));
    Tracking.trackOpen(this._config.client);
  }

  ask(question) {
    if (!question || typeof question !== 'string' || !question.trim()) {
      if (!this._open) {
        this.open();
      }
      return;
    }
    if (this._open && this._api) {
      this._api.send(question);
      this._scrollIntoViewIfNeeded();
    } else {
      this.open(question);
    }
    this.dispatchEvent(new CustomEvent('mta:ask', { detail: { question } }));
  }

  prefill(question) {
    if (!this._open) {
      this.open();
    } else {
      this._scrollIntoViewIfNeeded();
    }
    setTimeout(() => {
      this._api?.prefill(question);
    }, 50);
  }

  close() {
    if (this._api?.abort) {
      this._api.abort();
    }
    this._open = false;
    this._api = null;
    try {
      sessionStorage.setItem('mta_chat_open', 'false');
    } catch (e) { }
    this._render();
    this.dispatchEvent(new CustomEvent('mta:close'));
  }

  toggle() {
    if (this._open) {
      this.close();
    } else {
      this.open();
    }
  }
}
